import { useEffect, useState } from "react";
import { assetsAPI } from "@/services/api";
import {
  Asset,
  AssetBase,
  AssetUpdate,
  AssetType,
} from "@/types/assets";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { Table } from "@/components/ui/shadcn/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import { Card } from "@/components/ui/shadcn/card";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash, Plus } from "lucide-react";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

export default function AssetsDashboard() {
  const { toast } = useToast();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [newAsset, setNewAsset] = useState<Omit<AssetBase, 'asset_type'> & { asset_type?: AssetType }>({
    name: "",
    code: "",
    display_decimals: 0,
    contract_decimals: 0,
    image_url: "",
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await assetsAPI.getAssets({
        list_not_enabled: true,
      });
      setAssets(response);

      if (import.meta.env.DEV) {
        console.log("Assets fetched successfully:", response.length);
      }
    } catch (error) {
      handleApiError("Fetch assets", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async () => {
    if (!newAsset.name || !newAsset.code || !newAsset.asset_type) {
      toast({
        title: "Validation Error",
        description: "Name, code and asset type are required!",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const assetToCreate: AssetBase = {
        ...newAsset,
        asset_type: newAsset.asset_type,
      };
      const response = await assetsAPI.createAsset(assetToCreate);
      showSuccessToast(toast, "Success", "Asset created successfully!");
      setDialogOpen(false);
      fetchAssets();
      resetForm();

      if (import.meta.env.DEV) {
        console.log("Asset created successfully:", response);
      }
    } catch (error) {
      handleApiError("Create Asset", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAsset = async (id: number, updateData: Partial<AssetUpdate>) => {
    setLoading(true);
    try {
      const response = await assetsAPI.updateAsset(id, updateData);
      showSuccessToast(toast, "Success", "Asset updated successfully!");
      setDialogOpen(false);
      fetchAssets();
      resetForm();

      if (import.meta.env.DEV) {
        console.log("Asset updated successfully:", response);
      }
    } catch (error) {
      handleApiError("Update Asset", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAsset = async () => {
    if (!selectedAsset) return;

    setLoading(true);
    try {
      const updateData: AssetUpdate = {
        name: selectedAsset.name,
        code: selectedAsset.code,
        display_decimals: selectedAsset.display_decimals,
        contract_decimals: selectedAsset.contract_decimals,
        image_url: selectedAsset.image_url || undefined,
        is_enabled: selectedAsset.is_enabled,
        asset_type: AssetType.CRYPTO,
      };
      await handleUpdateAsset(selectedAsset.id, updateData);
    } catch (error) {
      handleApiError("Update Asset", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (id: number, isEnabled: boolean) => {
    await handleUpdateAsset(id, { is_enabled: isEnabled });
  };

  const resetForm = () => {
    setNewAsset({
      name: "",
      code: "",
      display_decimals: 0,
      contract_decimals: 0,
      image_url: "",
    });
    setSelectedAsset(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Assets Management
        </h1>
        <Card className="p-6 shadow-lg rounded-xl">
          <div className="flex justify-between mb-4">
            <Button
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-5 w-5" /> Add Asset
            </Button>
          </div>
          {loading ? (
            <p className="text-gray-500 text-center">Loading assets...</p>
          ) : (
            <Table className="w-full border rounded-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Display Decimals</th>
                  <th className="p-2 text-left">Contract Decimals</th>
                  <th className="p-2 text-left">Image URL</th>
                  <th className="p-2 text-left">Asset Type</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">{asset.code}</td>
                    <td className="p-2">{asset.name}</td>
                    <td className="p-2">{asset.display_decimals}</td>
                    <td className="p-2">{asset.contract_decimals}</td>
                    <td className="p-2">
                      {asset.image_url ? (
                        <a
                          href={asset.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          View Image
                        </a>
                      ) : (
                        <span className="text-gray-400">No image</span>
                      )}
                    </td>
                    <td className="p-2">
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        {asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1)}
                      </span>
                    </td>
                    <td className="p-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          asset.is_enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {asset.is_enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2 flex gap-2">
                      <Button
                        variant="outline"
                        className="flex items-center"
                        onClick={() => {
                          setSelectedAsset(asset);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        className={`flex items-center ${asset.is_enabled ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}`}
                        onClick={() => handleToggleEnabled(asset.id, !asset.is_enabled)}
                      >
                        {asset.is_enabled ? "Disable" : "Enable"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Asset Dialog (Create & Edit) */}
        {isDialogOpen && (
          <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-[450px] rounded-lg shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedAsset ? "Edit Asset" : "Create Asset"}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {selectedAsset
                    ? "Update the asset details below."
                    : "Fill in the asset details below to create a new asset."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {!selectedAsset && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Asset Type</label>
                      <div className="flex space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio h-4 w-4 text-blue-600"
                            checked={newAsset.asset_type === AssetType.CRYPTO}
                            onChange={() => setNewAsset({ ...newAsset, asset_type: AssetType.CRYPTO })}
                          />
                          <span className="ml-2">Crypto</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio h-4 w-4 text-blue-600"
                            checked={newAsset.asset_type === AssetType.FIAT}
                            onChange={() => setNewAsset({ ...newAsset, asset_type: AssetType.FIAT })}
                          />
                          <span className="ml-2">Fiat</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio h-4 w-4 text-blue-600"
                            checked={newAsset.asset_type === AssetType.STABLE}
                            onChange={() => setNewAsset({ ...newAsset, asset_type: AssetType.STABLE })}
                          />
                          <span className="ml-2">Stablecoin</span>
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Code</label>
                      <Input
                        className="w-full"
                        placeholder="Enter asset code (e.g., BTC, ETH, USD)"
                        value={newAsset.code}
                        onChange={(e) =>
                          setNewAsset({
                            ...newAsset,
                            code: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <Input
                    className="w-full"
                    placeholder="Enter asset name"
                    value={selectedAsset ? selectedAsset.name : newAsset.name}
                    onChange={(e) =>
                      selectedAsset
                        ? setSelectedAsset({
                            ...selectedAsset,
                            name: e.target.value,
                          })
                        : setNewAsset({ ...newAsset, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Display Decimals</label>
                  <Input
                    className="w-full"
                    type="number"
                    min="0"
                    max="18"
                    placeholder="Enter number of display decimals"
                    value={
                      selectedAsset
                        ? selectedAsset.display_decimals
                        : newAsset.display_decimals
                    }
                    onChange={(e) =>
                      selectedAsset
                        ? setSelectedAsset({
                            ...selectedAsset,
                            display_decimals: parseInt(e.target.value),
                          })
                        : setNewAsset({
                            ...newAsset,
                            display_decimals: parseInt(e.target.value),
                          })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Contract Decimals</label>
                  <Input
                    className="w-full"
                    type="number"
                    min="0"
                    max="18"
                    placeholder="Enter number of contract decimals"
                    value={
                      selectedAsset
                        ? selectedAsset.contract_decimals
                        : newAsset.contract_decimals
                    }
                    onChange={(e) =>
                      selectedAsset
                        ? setSelectedAsset({
                            ...selectedAsset,
                            contract_decimals: parseInt(e.target.value),
                          })
                        : setNewAsset({
                            ...newAsset,
                            contract_decimals: parseInt(e.target.value),
                          })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Image URL</label>
                  <Input
                    className="w-full"
                    placeholder="Enter image URL for the asset logo"
                    value={
                      selectedAsset ? selectedAsset.image_url || "" : newAsset.image_url
                    }
                    onChange={(e) =>
                      selectedAsset
                        ? setSelectedAsset({
                            ...selectedAsset,
                            image_url: e.target.value,
                          })
                        : setNewAsset({ ...newAsset, image_url: e.target.value })
                    }
                  />
                </div>
                {selectedAsset && (
                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="is_enabled"
                      checked={selectedAsset.is_enabled}
                      onChange={(e) =>
                        setSelectedAsset({
                          ...selectedAsset,
                          is_enabled: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="is_enabled"
                      className="text-sm font-medium text-gray-700"
                    >
                      Active
                    </label>
                  </div>
                )}
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      selectedAsset ? handleEditAsset : handleCreateAsset
                    }
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  >
                    {loading
                      ? selectedAsset
                        ? "Updating..."
                        : "Creating..."
                      : selectedAsset
                      ? "Update"
                      : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
}
